import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Input,
  Pagination,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import moment from "moment";
import { useSelector } from "react-redux";

import PageHeader from "../../../component/pageHeaders";
import { API } from "../../../config/API";
import { GET } from "../../../utils/apirequest";
import axios from "../../../config/axiosInstance";
import { formatMoney } from "../../../config/countries";

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface Registrar {
  id?: number;
  username: string;
  email?: string;
  code?: string | null;
  role?: { id: number; name: string } | null;
}

interface PlanInfo {
  id: number;
  plan_name: string;
  type?: string;
  price?: number | string;
  duration_type?: "days" | "months" | "years";
  duration_value?: number;
  region?: "india" | "gulf" | null;
}

interface EpaperUserRef {
  id: number;
  full_name?: string | null;
  email?: string | null;
  phone?: string | null;
  subscriber_code?: string | null;
}

interface Subscription {
  id: number;
  amount: string;
  payment_mode: string;
  payment_date: string;
  receipt_number: string;
  payment_reference?: string;
  subscription_start_date: string;
  subscription_end_date: string;
  status: string;
  register_mode?: string;
  zone_code?: string;
  agent_code?: string;
  notes?: string;
  created_by_admin?: Registrar | null;
  subscription_plan?: PlanInfo | null;
  epaper_user?: EpaperUserRef | null;
  createdAt: string;
}

interface PaginationInfo {
  page: number;
  pageSize: number;
  total: number;
  pageCount: number;
}

const paymentModeLabels: Record<string, string> = {
  cash: "Cash",
  online: "Online (PayTabs)",
  bank_transfer: "Bank Transfer",
  cheque: "Cheque",
  upi: "UPI",
};

const roleSuffix = (roleId?: number) => {
  if (roleId === 4) return "Admin";
  if (roleId === 5) return "Agent";
  if (roleId === 6) return "E-Paper Admin";
  return "";
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "active":
      return "green";
    case "expired":
      return "orange";
    case "cancelled":
      return "red";
    case "refunded":
      return "volcano";
    default:
      return "default";
  }
};

const SubscriptionsHistoryScreen: React.FC = () => {
  const navigate = useNavigate();
  const User = useSelector((state: any) => state?.User);
  const isAdmin = User?.user?.role?.id === 4 || User?.user?.role?.id === 6;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Subscription[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    pageSize: 20,
    total: 0,
    pageCount: 0,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [agentFilter, setAgentFilter] = useState<string[]>([]);
  const [dateRange, setDateRange] =
    useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  const [agentsList, setAgentsList] = useState<
    { value: string; label: string }[]
  >([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentSearch, setAgentSearch] = useState("");
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const fetchAgents = async (search: string = "") => {
    try {
      setAgentsLoading(true);
      const params = new URLSearchParams({
        page: "1",
        pageSize: "500",
        roles: "4,5,6",
        ...(search && { search }),
      });
      const res: any = await GET(
        `${API.EPAPER_AGENTS_LIST}?${params.toString()}`,
        null,
        null
      );
      if (res && res.data) {
        setAgentsList(
          res.data
            .filter((agent: any) => !!agent.code)
            .map((agent: any) => {
              const role = roleSuffix(agent.role?.id);
              const rolePart = role ? ` - ${role}` : "";
              return {
                value: agent.code,
                label: `${agent.username} (${agent.code})${rolePart}`,
              };
            })
        );
      }
    } catch (error) {
      console.error("Error fetching agents:", error);
    } finally {
      setAgentsLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    const t = setTimeout(() => {
      fetchAgents(agentSearch);
    }, 400);
    return () => clearTimeout(t);
  }, [agentSearch]);

  const fetchSubscriptions = async (
    page: number = 1,
    pageSize: number = 20,
    search: string = "",
    agentCodes: string[] = [],
    range?: [dayjs.Dayjs, dayjs.Dayjs] | null
  ) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        ...(search && { search }),
        ...(agentCodes.length && { agentCodes: agentCodes.join(",") }),
        ...(!isAdmin && User?.user?.agent_code
          ? { agentCodes: User.user.agent_code }
          : {}),
      });

      if (range) {
        params.append("startDate", range[0].startOf("day").toISOString());
        params.append("endDate", range[1].endOf("day").toISOString());
      }

      const response: any = await GET(
        `${API.EPAPER_USERS_SUBSCRIPTIONS_LIST}?${params.toString()}`,
        null,
        null
      );

      if (response && response.data) {
        setData(response.data);
        setPagination(response.pagination);
      }
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      message.error("Failed to fetch subscriptions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscriptions(1, 20);
  }, []);

  const applyFilters = () => {
    fetchSubscriptions(
      1,
      pagination.pageSize,
      searchTerm,
      agentFilter,
      dateRange
    );
  };

  const handlePageChange = (page: number, pageSize: number) => {
    fetchSubscriptions(page, pageSize, searchTerm, agentFilter, dateRange);
  };

  const handleReset = () => {
    setSearchTerm("");
    setAgentFilter([]);
    setDateRange(null);
    fetchSubscriptions(1, pagination.pageSize, "", [], null);
  };

  const handleDownloadReceipt = async (record: Subscription) => {
    const userId = record.epaper_user?.id;
    if (!userId) {
      message.error("Cannot download receipt: missing user reference");
      return;
    }
    setDownloadingId(record.id);
    try {
      const res = await axios.get(
        `${API.BASE_URL}epaper-users/${userId}/subscriptions/${record.id}/receipt`,
        { responseType: "blob" }
      );
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(record.receipt_number || String(record.id)).replace(
        /\//g,
        "-"
      )}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download receipt error:", err);
      message.error("Failed to download receipt");
    } finally {
      setDownloadingId(null);
    }
  };

  const columns = [
    {
      title: "S.No",
      key: "serial",
      width: 60,
      render: (_: any, __: any, index: number) =>
        (pagination.page - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "User",
      key: "user",
      width: 180,
      render: (_: any, record: Subscription) => {
        const u = record.epaper_user;
        if (!u) return <span style={{ color: "#999" }}>—</span>;
        return (
          <div>
            <div>{u.full_name || "—"}</div>
            {u.subscriber_code && (
              <div
                style={{
                  fontSize: 11,
                  color: "#0576b7",
                  fontFamily: "monospace",
                }}
              >
                {u.subscriber_code}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Email",
      key: "email",
      width: 200,
      render: (_: any, record: Subscription) => {
        const email = record.epaper_user?.email;
        return email ? (
          <span style={{ fontSize: 12 }}>{email}</span>
        ) : (
          <span style={{ color: "#999" }}>—</span>
        );
      },
    },
    {
      title: "Phone",
      key: "phone",
      width: 150,
      render: (_: any, record: Subscription) => {
        const phone = record.epaper_user?.phone;
        return phone ? (
          <span style={{ fontSize: 12 }}>{phone}</span>
        ) : (
          <span style={{ color: "#999" }}>—</span>
        );
      },
    },
    {
      title: "Receipt #",
      dataIndex: "receipt_number",
      key: "receipt_number",
      width: 240,
      render: (text: string) => (
        <span style={{ fontFamily: "monospace" }}>{text || "N/A"}</span>
      ),
    },
    {
      title: "Agent",
      key: "agent",
      width: 200,
      render: (_: any, record: Subscription) => {
        const admin = record.created_by_admin;
        const code = record.agent_code;
        if (!admin && !code) {
          return <span style={{ color: "#999" }}>—</span>;
        }
        return (
          <div>
            {admin?.username && <div>{admin.username}</div>}
            {code && (
              <div
                style={{
                  fontFamily: "monospace",
                  color: "#0576b7",
                  fontSize: 12,
                }}
              >
                {code}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 100,
      render: (amount: string, record: any) => (
        <span>{formatMoney(amount, record?.currency)}</span>
      ),
    },
    {
      title: "Payment Mode",
      dataIndex: "payment_mode",
      key: "payment_mode",
      width: 140,
      render: (mode: string) => (
        <Tag color="blue">{paymentModeLabels[mode] || mode}</Tag>
      ),
    },
    {
      title: "Payment Date",
      dataIndex: "payment_date",
      key: "payment_date",
      width: 130,
      render: (date: string) =>
        date ? moment(date).format("DD-MM-YYYY") : "N/A",
    },
    {
      title: "Start Date",
      dataIndex: "subscription_start_date",
      key: "subscription_start_date",
      width: 130,
      render: (date: string) =>
        date ? moment(date).format("DD-MM-YYYY") : "N/A",
    },
    {
      title: "End Date",
      dataIndex: "subscription_end_date",
      key: "subscription_end_date",
      width: 130,
      render: (date: string) =>
        date ? moment(date).format("DD-MM-YYYY") : "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: string) => (
        <Tag
          color={getStatusColor(status)}
          style={{ textTransform: "capitalize" }}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Plan",
      dataIndex: "subscription_plan",
      key: "subscription_plan",
      width: 180,
      render: (plan: PlanInfo | null | undefined) => {
        if (!plan) return <span style={{ color: "#999" }}>—</span>;
        return <div>{plan.plan_name}</div>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 90,
      fixed: "right" as const,
      render: (_: any, record: Subscription) => (
        <Space size="small">
          {downloadingId === record.id ? (
            <Spin size="small" />
          ) : (
            <Tooltip title="Download Invoice">
              <Button
                type="text"
                size="small"
                icon={
                  <DownloadOutlined
                    style={{ color: "#0576b7", fontSize: 18 }}
                  />
                }
                onClick={() => handleDownloadReceipt(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="p-4">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <PageHeader
          title="Subscription History"
          loader={0}
          length="Dashboard / E-Paper Users / Subscription History"
        />
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/Auth/epaper-users")}
          style={{ marginTop: 4 }}
        >
          Back
        </Button>
      </div>
      <br />

      <Card className="mb-4">
        <Row gutter={[12, 12]} align="bottom">
          <Col xs={24} sm={12} md={isAdmin ? 7 : 9}>
            <p style={{ marginBottom: 4, fontWeight: 600, fontSize: 13 }}>
              User Name Search
            </p>
            <Search
              placeholder="Name, email, phone, code or receipt #"
              allowClear
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onSearch={applyFilters}
              onPressEnter={applyFilters}
            />
          </Col>

          {isAdmin && (
            <Col xs={24} sm={12} md={6}>
              <p style={{ marginBottom: 4, fontWeight: 600, fontSize: 13 }}>
                Agent
              </p>
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder="All Agents / Admins"
                allowClear
                loading={agentsLoading}
                maxTagCount="responsive"
                value={agentFilter}
                onChange={(values: string[]) => {
                  if (values.includes("all")) {
                    setAgentFilter([]);
                  } else {
                    setAgentFilter(values);
                  }
                }}
                options={[{ value: "all", label: "All" }, ...agentsList]}
                showSearch
                filterOption={false}
                onSearch={setAgentSearch}
                onBlur={() => setAgentSearch("")}
                notFoundContent={agentsLoading ? "Searching..." : "No match"}
              />
            </Col>
          )}

          <Col xs={24} sm={12} md={isAdmin ? 7 : 9}>
            <p style={{ marginBottom: 4, fontWeight: 600, fontSize: 13 }}>
              Payment Date
            </p>
            <RangePicker
              style={{ width: "100%" }}
              value={dateRange}
              onChange={(dates) =>
                setDateRange(
                  dates as [dayjs.Dayjs, dayjs.Dayjs] | null
                )
              }
            />
          </Col>

          <Col
            xs={24}
            sm={24}
            md={isAdmin ? 4 : 6}
            style={{ display: "flex", gap: 8, alignItems: "flex-end" }}
          >
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={applyFilters}
              style={{ flex: 1 }}
            >
              Search
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleReset}
              title="Reset filters"
            />
          </Col>
        </Row>
      </Card>

      <Card>
        <div style={{ marginBottom: 8 }} className="text-sm text-gray-600">
          Showing{" "}
          {pagination.total === 0
            ? 0
            : (pagination.page - 1) * pagination.pageSize + 1}{" "}
          to{" "}
          {Math.min(pagination.page * pagination.pageSize, pagination.total)} of{" "}
          {pagination.total} subscriptions
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin size="large" />
          </div>
        ) : data.length === 0 ? (
          <Empty description="No subscriptions found" />
        ) : (
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ x: "max-content" }}
            className="border border-gray-200 rounded-lg"
          />
        )}

        <div className="flex justify-end mt-4">
          <Pagination
            current={pagination.page}
            total={pagination.total}
            pageSize={pagination.pageSize}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} subscriptions`
            }
            onChange={handlePageChange}
            pageSizeOptions={["10", "20", "50", "100"]}
            className="text-sm"
          />
        </div>
      </Card>
    </div>
  );
};

export default SubscriptionsHistoryScreen;
